import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { API_BASE_URL } from '../lib/api';

interface AccountFormData {
  accountId: string;
  accountName: string;
  accountNameKana: string;
  tradeStatus: string;
  postalCode: string;
  prefecture: string;
  city: string;
  street: string;
  building: string;
  telMain: string;
  faxMain: string;
  website: string;
  industryType: string;
  employeeCount: string;
  capital: string;
  fiscalMonth: string;
  remarks: string;
}

const initialFormData: AccountFormData = {
  accountId: '',
  accountName: '',
  accountNameKana: '',
  tradeStatus: '見込み',
  postalCode: '',
  prefecture: '',
  city: '',
  street: '',
  building: '',
  telMain: '',
  faxMain: '',
  website: '',
  industryType: '',
  employeeCount: '',
  capital: '',
  fiscalMonth: '',
  remarks: '',
};

const tradeStatusOptions = ['取引中', '見込み', '休眠', '失注'];
const prefectureOptions = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

export function AccountForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [formData, setFormData] = useState<AccountFormData>(initialFormData);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchAccount();
    } else {
      // 新規作成時に企業IDを自動生成
      const newId = `ACC-${Date.now().toString(36).toUpperCase()}`;
      setFormData(prev => ({ ...prev, accountId: newId }));
    }
  }, [id]);

  const fetchAccount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/${id}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('企業情報の取得に失敗しました');
      }
      
      const data = await response.json();
      const account = data.account;
      
      setFormData({
        accountId: account.accountId || '',
        accountName: account.accountName || '',
        accountNameKana: account.accountNameKana || '',
        tradeStatus: account.tradeStatus || '見込み',
        postalCode: account.postalCode || '',
        prefecture: account.prefecture || '',
        city: account.city || '',
        street: account.street || '',
        building: account.building || '',
        telMain: account.telMain || '',
        faxMain: account.faxMain || '',
        website: account.website || '',
        industryType: account.industryType || '',
        employeeCount: account.employeeCount || '',
        capital: account.capital || '',
        fiscalMonth: account.fiscalMonth?.toString() || '',
        remarks: account.remarks || '',
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const url = isEdit 
        ? `${API_BASE_URL}/api/accounts/${id}`
        : `${API_BASE_URL}/api/accounts`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          fiscalMonth: formData.fiscalMonth ? parseInt(formData.fiscalMonth) : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '保存に失敗しました');
      }

      navigate(`/accounts/${data.id || id}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {isEdit ? '企業を編集' : '企業を新規作成'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="space-y-6 pt-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* 基本情報 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="企業ID"
                name="accountId"
                value={formData.accountId}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  取引状況
                </label>
                <select
                  name="tradeStatus"
                  value={formData.tradeStatus}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {tradeStatusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="企業名 *"
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
                required
              />
              <Input
                label="企業名（かな）"
                name="accountNameKana"
                value={formData.accountNameKana}
                onChange={handleChange}
              />
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 住所 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="郵便番号"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                placeholder="000-0000"
              />
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  都道府県
                </label>
                <select
                  name="prefecture"
                  value={formData.prefecture}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {prefectureOptions.map(pref => (
                    <option key={pref} value={pref}>{pref}</option>
                  ))}
                </select>
              </div>
              <Input
                label="市区町村"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="番地"
                name="street"
                value={formData.street}
                onChange={handleChange}
              />
              <Input
                label="建物名"
                name="building"
                value={formData.building}
                onChange={handleChange}
              />
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 連絡先 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="電話番号"
                name="telMain"
                value={formData.telMain}
                onChange={handleChange}
                placeholder="03-0000-0000"
              />
              <Input
                label="FAX番号"
                name="faxMain"
                value={formData.faxMain}
                onChange={handleChange}
                placeholder="03-0000-0000"
              />
            </div>

            <Input
              label="Webサイト"
              name="website"
              type="url"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://example.com"
            />

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 会社情報 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="業種"
                name="industryType"
                value={formData.industryType}
                onChange={handleChange}
              />
              <Input
                label="従業員数"
                name="employeeCount"
                value={formData.employeeCount}
                onChange={handleChange}
              />
              <Input
                label="資本金"
                name="capital"
                value={formData.capital}
                onChange={handleChange}
              />
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  決算月
                </label>
                <select
                  name="fiscalMonth"
                  value={formData.fiscalMonth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                    <option key={month} value={month}>{month}月</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                備考
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg resize-none"
                style={{
                  backgroundColor: 'var(--background-card)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {/* ボタン */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                キャンセル
              </Button>
              <Button
                type="submit"
                leftIcon={<Save size={16} />}
                isLoading={isSaving}
              >
                {isEdit ? '更新' : '作成'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
